import { PlaybookNode } from "@/hooks/usePlaybooks";

export interface TreeNode {
  node: PlaybookNode;
  x: number;
  y: number;
  children: TreeNode[];
  level: number;
}

export interface LayoutConfig {
  nodeWidth: number;
  nodeHeight: number;
  horizontalSpacing: number;
  verticalSpacing: number;
}

const DEFAULT_CONFIG: LayoutConfig = {
  nodeWidth: 280,
  nodeHeight: 140,
  horizontalSpacing: 40,
  verticalSpacing: 80,
};

/**
 * Build a hierarchical tree structure from a flat list of playbook nodes.
 * The "start" node is the root.
 */
export function buildTreeFromNodes(
  nodes: PlaybookNode[],
  config: LayoutConfig = DEFAULT_CONFIG
): { tree: TreeNode | null; nodeMap: Map<string, TreeNode>; canvasSize: { width: number; height: number } } {
  if (!nodes || nodes.length === 0) {
    return { tree: null, nodeMap: new Map(), canvasSize: { width: 0, height: 0 } };
  }

  // Create a map for quick lookup
  const nodeByNodeId = new Map<string, PlaybookNode>();
  nodes.forEach((n) => nodeByNodeId.set(n.node_id, n));

  // Find the start node
  const startNode = nodes.find((n) => n.node_id === "start");
  if (!startNode) {
    // If no start node, return the first node as root
    const firstNode = nodes[0];
    const singleTreeNode: TreeNode = {
      node: firstNode,
      x: 0,
      y: 0,
      children: [],
      level: 0,
    };
    const nodeMap = new Map<string, TreeNode>();
    nodeMap.set(firstNode.node_id, singleTreeNode);
    return { 
      tree: singleTreeNode, 
      nodeMap, 
      canvasSize: { width: config.nodeWidth + 100, height: config.nodeHeight + 100 } 
    };
  }

  // Track visited nodes to prevent cycles
  const visited = new Set<string>();
  const nodeMap = new Map<string, TreeNode>();

  // Recursively build tree
  function buildSubtree(nodeId: string, level: number): TreeNode | null {
    if (visited.has(nodeId)) return null;
    
    const node = nodeByNodeId.get(nodeId);
    if (!node) return null;

    visited.add(nodeId);

    const treeNode: TreeNode = {
      node,
      x: 0,
      y: 0,
      children: [],
      level,
    };

    nodeMap.set(nodeId, treeNode);

    // Build children from options
    if (node.options && !node.is_end) {
      for (const opt of node.options) {
        if (opt.nextNode && !visited.has(opt.nextNode)) {
          const child = buildSubtree(opt.nextNode, level + 1);
          if (child) {
            treeNode.children.push(child);
          }
        }
      }
    }

    return treeNode;
  }

  const tree = buildSubtree("start", 0);

  // Calculate positions using a simple level-based layout
  if (tree) {
    calculatePositions(tree, config);
  }

  // Calculate canvas size
  let maxX = 0;
  let maxY = 0;
  nodeMap.forEach((tn) => {
    maxX = Math.max(maxX, tn.x + config.nodeWidth);
    maxY = Math.max(maxY, tn.y + config.nodeHeight);
  });

  // Add orphan nodes (nodes not connected to the tree)
  const orphanNodes = nodes.filter((n) => !visited.has(n.node_id));
  let orphanY = maxY + config.verticalSpacing;
  orphanNodes.forEach((orphan, index) => {
    const orphanTreeNode: TreeNode = {
      node: orphan,
      x: index * (config.nodeWidth + config.horizontalSpacing),
      y: orphanY,
      children: [],
      level: -1, // Mark as orphan
    };
    nodeMap.set(orphan.node_id, orphanTreeNode);
    maxX = Math.max(maxX, orphanTreeNode.x + config.nodeWidth);
    maxY = Math.max(maxY, orphanTreeNode.y + config.nodeHeight);
  });

  return {
    tree,
    nodeMap,
    canvasSize: { width: maxX + 100, height: maxY + 100 },
  };
}

/**
 * Calculate node positions using a level-based layout.
 * Each level is a row, nodes are spread horizontally.
 */
function calculatePositions(tree: TreeNode, config: LayoutConfig): void {
  // First, count nodes at each level
  const levelCounts = new Map<number, number>();
  const levelNodes = new Map<number, TreeNode[]>();

  function countLevels(node: TreeNode) {
    const count = levelCounts.get(node.level) || 0;
    levelCounts.set(node.level, count + 1);

    const nodesAtLevel = levelNodes.get(node.level) || [];
    nodesAtLevel.push(node);
    levelNodes.set(node.level, nodesAtLevel);

    node.children.forEach(countLevels);
  }

  countLevels(tree);

  // Position nodes at each level
  levelNodes.forEach((nodesAtLevel, level) => {
    const totalWidth =
      nodesAtLevel.length * config.nodeWidth +
      (nodesAtLevel.length - 1) * config.horizontalSpacing;
    const startX = -totalWidth / 2 + config.nodeWidth / 2;

    nodesAtLevel.forEach((node, index) => {
      node.x = startX + index * (config.nodeWidth + config.horizontalSpacing);
      node.y = level * (config.nodeHeight + config.verticalSpacing);
    });
  });

  // Normalize to start from positive coordinates
  let minX = Infinity;
  levelNodes.forEach((nodesAtLevel) => {
    nodesAtLevel.forEach((node) => {
      minX = Math.min(minX, node.x);
    });
  });

  const offsetX = -minX + 50;
  levelNodes.forEach((nodesAtLevel) => {
    nodesAtLevel.forEach((node) => {
      node.x += offsetX;
    });
  });
}

/**
 * Get connections between nodes for drawing lines
 */
export interface Connection {
  from: { x: number; y: number };
  to: { x: number; y: number };
  fromNodeId: string;
  toNodeId: string;
  optionLabel: string;
  optionVariant?: string;
}

export function getConnections(
  nodeMap: Map<string, TreeNode>,
  config: LayoutConfig = DEFAULT_CONFIG
): Connection[] {
  const connections: Connection[] = [];

  nodeMap.forEach((treeNode) => {
    const node = treeNode.node;
    if (!node.options || node.is_end) return;

    node.options.forEach((opt) => {
      if (!opt.nextNode) return;
      
      const targetNode = nodeMap.get(opt.nextNode);
      if (!targetNode) return;

      connections.push({
        from: {
          x: treeNode.x + config.nodeWidth / 2,
          y: treeNode.y + config.nodeHeight,
        },
        to: {
          x: targetNode.x + config.nodeWidth / 2,
          y: targetNode.y,
        },
        fromNodeId: node.node_id,
        toNodeId: opt.nextNode,
        optionLabel: opt.label,
        optionVariant: opt.variant,
      });
    });
  });

  return connections;
}
